import React, { useReducer } from "react";

import Constants from "./Constants";
import StepMenuItem from "./StepMenuItem";
import ViewMap from "./ViewMap";
import PathMap from "./Paths";

import { useAccountState } from "../../hooks/query/useAccount";
import useGetSetupRequest from "./hooks/useGetSetupRequest";
import UserActions from "../../actions/UserActions";

import LoadingSpinner from "../LoadingSpinner";
import SiteActions from "../../actions/SiteActions";
import styles from "./BlogSetupWizard.module.css";

const blogSetupState = {
  path: Constants.PATH_NEW,
  pathIndex: 0,
  view: Constants.VIEW_CHOOSE_SETUP_TYPE,
  migrationRequired: false,
  setupData: {
    blog_domain_name: "",
    blog_url: "",
    blog_admin_url: "",
    blog_admin_user: "",
    blog_admin_pass: "",
    blog_admin_verified: false,
    blog_registrar_name: "",
    blog_registrar_url: "",
    blog_registrar_user: "",
    blog_registrar_pass: "",
    blog_new_url: "",
  },
  setupRequestId: undefined,
  primaryDomain: undefined,
  userId: undefined,
  isNewRequest: true,
};

function initAction(state, payload) {
  const { migrationRequired, setupRequestId } = payload;

  let { path, pathIndex, view } = state;

  if (state.path === undefined) {
    if (migrationRequired) {
      path = setupRequestId ? Constants.PATH_MIGRATION_UPDATE : Constants.PATH_MIGRATION;
    } else {
      path = setupRequestId ? Constants.PATH_NEW_UPDATE : Constants.PATH_NEW;
    }
    pathIndex = 0;
    view = PathMap[path][pathIndex];
  }

  return {
    ...state,
    path,
    pathIndex,
    view,
    migrationRequired,
    setupData: { ...state.setupData, ...payload.setupData },
    setupRequestId,
  };
}

function updateSetupData(state, payload) {
  return { ...state, setupData: { ...state.setupData, ...payload } };
}

function nextStep(state, payload) {
  return goToNewStep(state, Math.min(PathMap[state.path].length - 1, state.pathIndex + 1), payload);
}

function previousStep(state, payload) {
  return goToNewStep(state, Math.max(0, state.pathIndex - 1), payload);
}

function goToNewStep(state, viewIndex, payload) {
  const nextView = PathMap[state.path][viewIndex];
  const newState = { ...state, view: nextView, pathIndex: viewIndex };
  return payload ? updateSetupData(newState, payload) : newState;
}

function choosePath(state, payload) {
  const viewIndex = 1;
  const view = PathMap[payload.path][viewIndex];
  const migrationRequired = payload.path === Constants.PATH_MIGRATION;
  return { ...state, path: payload.path, view, pathIndex: viewIndex, migrationRequired };
}

function blogSetupReducer(state, action) {
  switch (action.type) {
    case "next":
      return nextStep(state, action.payload);
    case "prev":
      return previousStep(state, action.payload);
    case "choosePath":
      return choosePath(state, action.payload);
    case "update":
      return updateSetupData(state, action.payload);
    case "notFound":
      return { view: Constants.VIEW_SETUP_NOT_FOUND };
    case "init":
      return initAction(state, action.payload);
    default:
      return state;
  }
}

const getPathSteps = ({ path, view }) => {
  const steps = PathMap[path];
  const items = steps
    ? steps.map((viewName) => {
        const setupStep = ViewMap.get(viewName);
        return setupStep ? (
          <StepMenuItem
            key={setupStep.view}
            text={setupStep.desc}
            active={setupStep.view === view}
          />
        ) : undefined;
      })
    : [];

  return (
    <div className={styles["navigation-pane"]}>
      <ol className={styles["navigation-list"]}>{items}</ol>
    </div>
  );
};

const getCurrentView = (blogSetup, actions) => {
  const { view } = blogSetup;
  const setupStep = ViewMap.get(view);

  return setupStep
    ? getViewComponent({ component: setupStep.comp, blogSetup, actions })
    : undefined;
};

const getViewComponent = ({ component, blogSetup, actions }) =>
  React.createElement(component, { blogSetup, actions });

function BlogSetupWizard(props) {
  const user = useAccountState();
  const [blogSetup, dispatch] = useReducer(blogSetupReducer, blogSetupState, (state) => ({
    ...state,
    primaryDomain: user.get("domain"),
    userId: user.get("user_id"),
    setupData: {
      ...state.setupData,
      blog_domain_name: user.get("domain"),
      blog_new_url: `${user.get("domain")}/blog`,
    },
    isNewRequest: !user.get("blogId"),
  }));

  const { isError, isLoading } = useGetSetupRequest(
    {
      userId: user.get("user_id"),
      blogId: user.get("blogId"),
    },
    {
      onSuccess: ({ setupRequest }) =>
        dispatch({ type: setupRequest ? "init" : "notFound", payload: setupRequest }),
    }
  );

  if (isLoading) {
    return (
      <div className="view-centered">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="view-centered" style={{ flexDirection: "column" }}>
        <div>A problem occurred. Please try again.</div>
      </div>
    );
  }

  const actions = {
    goToNextStep: (data) => {
      dispatch({
        type: "next",
        payload: data,
      });
    },
    goToPrevStep: (data) => {
      dispatch({
        type: "prev",
        payload: data,
      });
    },
    updateSetup: (data) => {
      dispatch({
        type: "update",
        payload: data,
      });
    },
    choosePath: (data) => {
      dispatch({
        type: "choosePath",
        payload: data,
      });
    },
    updateUser: (configuration) => {
      UserActions.updateConfiguration(configuration);
    },
    assignBlogToSite: (configuration) => {
      SiteActions.domainConnectBlog(configuration);
    },
    close: () => {
      if (props.onComplete) {
        props.onComplete();
      }
    },
  };

  const steps = getPathSteps(blogSetup);
  const view = getCurrentView(blogSetup, actions);

  return (
    <div className={styles["main-panel"]} style={{ display: "flex", height: "500" }}>
      {steps}
      {view}
    </div>
  );
}

export default BlogSetupWizard;
